import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle,
    Button, TextField, Box, Alert, MenuItem, FormControl, InputLabel, Select
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';

export default function ArchiveHotelModal({ open, onClose, onSave, hotel }) {
    const [reason, setReason] = useState('Other');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setReason('Other');
            setNotes('');
            setError(null);
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!reason) {
            setError('Reason is required');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            await onSave({
                id: hotel.id,
                tenantId: hotel.tenantId,
                reason,
                notes: notes.trim() || undefined,
            });
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to archive hotel');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={isSubmitting ? undefined : onClose}
            PaperProps={{ sx: { borderRadius: 3, padding: 1, minWidth: 440 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                <ArchiveIcon color="warning" /> Archive Hotel
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    {hotel && (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                            You are about to archive <strong>{hotel.name}</strong>. This action will deactivate the property.
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
                    )}

                    <FormControl fullWidth size="small" required>
                        <InputLabel>Reason</InputLabel>
                        <Select
                            value={reason}
                            label="Reason"
                            onChange={(e) => setReason(e.target.value)}
                            disabled={isSubmitting}
                        >
                            <MenuItem value="Permanently Closed">Permanently Closed</MenuItem>
                            <MenuItem value="Sold">Sold</MenuItem>
                            <MenuItem value="Merged">Merged</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label="Notes"
                        placeholder="e.g. Property sold to XYZ"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={isSubmitting}
                        size="small"
                        multiline
                        rows={3}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '0 24px 16px 24px' }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="warning"
                    sx={{ borderRadius: 2 }}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Archiving...' : 'Archive'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
